import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthLayout } from "@/components/Brand";
import { Field, FormError, SubmitButton, LoadingState, ErrorState } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

function Success({ text }) {
  return (
    <div className="space-y-5" data-testid="auth-success">
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> <p>{text}</p>
      </div>
      <Button asChild className="h-11 w-full" data-testid="go-to-login-button"><Link to="/login">{t("auth.loginButton")}</Link></Button>
    </div>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError(t("auth.passwordMismatch"));
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.resetTitle")} subtitle={t("auth.passwordRule")}>
      {done ? <Success text={t("auth.resetDone")} /> : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <FormError message={error} />
          <Field label={t("auth.newPassword")} htmlFor="reset-pw">
            <Input id="reset-pw" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="reset-password-input" />
          </Field>
          <Field label={t("auth.confirmPassword")} htmlFor="reset-pw2">
            <Input id="reset-pw2" type="password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11" data-testid="reset-password-confirm-input" />
          </Field>
          <SubmitButton busy={busy} className="h-11 w-full" data-testid="reset-submit-button">{t("auth.resetButton")}</SubmitButton>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmail() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token })
      .then(() => setState({ ok: true }))
      .catch((e) => setState({ error: errMsg(e) }));
  }, [token]);

  return (
    <AuthLayout title={t("auth.verifyTitle")}>
      {state.loading && <LoadingState />}
      {state.ok && <Success text={t("auth.verifyDone")} />}
      {state.error && <ErrorState message={state.error} />}
    </AuthLayout>
  );
}
